/**
 * Focus Areas API Service
 *
 * API client for /api/v1/focus-areas. All endpoints authenticate via apiClient.
 * Phase 1 surface: list / create / update / edit practice / retire (no AI).
 * Phase 3 additions: check-in start, finalize, update, history, overdue list,
 * and streaming check-in message (SSE).
 */

import { apiClient, getCurrentAccessToken } from "./api";
import type {
  FocusArea,
  FocusAreaCreatePayload,
  FocusAreaListResponse,
  FocusAreaPractice,
  FocusAreaPracticeUpdatePayload,
  FocusAreaRetirePayload,
  FocusAreaStatusFilter,
  FocusAreaUpdatePayload,
} from "@/types/focusArea";
import type {
  FocusAreaCheckIn,
  FocusAreaCheckInFinalizeResponse,
  FocusAreaCheckInHistoryResponse,
  FocusAreaCheckInStartResponse,
  FocusAreaCheckInUpdate,
} from "@/types/focusAreaCheckIn";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";
const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";
const BASE = `${API_PREFIX}/focus-areas`;

export async function listFocusAreas(
  status: FocusAreaStatusFilter = "active"
): Promise<FocusAreaListResponse> {
  const response = await apiClient.get<FocusAreaListResponse>(`${BASE}/`, {
    params: { status },
  });
  return response.data;
}

export async function activateFocusArea(
  focusAreaId: string
): Promise<FocusArea> {
  const response = await apiClient.post<FocusArea>(
    `${BASE}/${focusAreaId}/activate`
  );
  return response.data;
}

export async function dismissFocusArea(
  focusAreaId: string
): Promise<{ id: string; dismissed: boolean }> {
  const response = await apiClient.post<{ id: string; dismissed: boolean }>(
    `${BASE}/${focusAreaId}/dismiss`
  );
  return response.data;
}

export async function regeneratePractice(
  focusAreaId: string
): Promise<FocusArea> {
  const response = await apiClient.post<FocusArea>(
    `${BASE}/${focusAreaId}/regenerate-practice`
  );
  return response.data;
}

export async function createFocusArea(
  payload: FocusAreaCreatePayload
): Promise<FocusArea> {
  const response = await apiClient.post(`${BASE}/`, payload);
  return response.data;
}

export async function updateFocusArea(
  focusAreaId: string,
  payload: FocusAreaUpdatePayload
): Promise<FocusArea> {
  const response = await apiClient.patch(`${BASE}/${focusAreaId}`, payload);
  return response.data;
}

export async function editFocusAreaPractice(
  focusAreaId: string,
  payload: FocusAreaPracticeUpdatePayload
): Promise<FocusAreaPractice> {
  const response = await apiClient.patch(
    `${BASE}/${focusAreaId}/practice`,
    payload
  );
  return response.data;
}

export async function suggestPractice(payload: {
  name: string;
  description: string;
  partner_id?: string | null;
}): Promise<{ practice_text: string }> {
  const response = await apiClient.post<{ practice_text: string }>(
    `${BASE}/suggest-practice`,
    payload
  );
  return response.data;
}

export async function retireFocusArea(
  focusAreaId: string,
  payload: FocusAreaRetirePayload
): Promise<FocusArea> {
  const response = await apiClient.post(
    `${BASE}/${focusAreaId}/retire`,
    payload
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Auto-retire endpoints (Phase 4)
// ---------------------------------------------------------------------------

export async function confirmAutoRetire(id: string): Promise<FocusArea> {
  const response = await apiClient.post<FocusArea>(
    `${BASE}/${id}/confirm-auto-retire`
  );
  return response.data;
}

export async function snoozeAutoRetire(id: string): Promise<FocusArea> {
  const response = await apiClient.post<FocusArea>(
    `${BASE}/${id}/snooze-auto-retire`
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Check-in endpoints (Phase 3)
// ---------------------------------------------------------------------------

export async function startCheckIn(
  focusAreaId: string
): Promise<FocusAreaCheckInStartResponse> {
  const response = await apiClient.post<FocusAreaCheckInStartResponse>(
    `${BASE}/${focusAreaId}/check-ins`
  );
  return response.data;
}

export async function finalizeCheckIn(
  checkInId: string
): Promise<FocusAreaCheckInFinalizeResponse> {
  const response = await apiClient.post<FocusAreaCheckInFinalizeResponse>(
    `${API_PREFIX}/check-ins/${checkInId}/finalize`
  );
  return response.data;
}

export async function updateCheckIn(
  checkInId: string,
  payload: FocusAreaCheckInUpdate
): Promise<FocusAreaCheckIn> {
  const response = await apiClient.patch<FocusAreaCheckIn>(
    `${API_PREFIX}/check-ins/${checkInId}`,
    payload
  );
  return response.data;
}

export async function getCheckIn(checkInId: string): Promise<FocusAreaCheckIn> {
  const response = await apiClient.get<FocusAreaCheckIn>(
    `${API_PREFIX}/check-ins/${checkInId}`
  );
  return response.data;
}

export async function listCheckIns(
  focusAreaId: string
): Promise<FocusAreaCheckInHistoryResponse> {
  const response = await apiClient.get<FocusAreaCheckInHistoryResponse>(
    `${BASE}/${focusAreaId}/check-ins`
  );
  return response.data;
}

export async function listOverdueCheckIns(): Promise<FocusArea[]> {
  const response = await apiClient.get<FocusArea[]>(
    `${BASE}/overdue-check-ins`
  );
  return response.data;
}

/**
 * Callback types for check-in streaming events
 */
export interface CheckInStreamingCallbacks {
  onContent?: (content: string) => void;
  onDone?: (data: {
    auto_finalized: boolean;
    proposed_summary: string | null;
    proposed_progress_signal: string | null;
  }) => void;
  onError?: (error: string) => void;
}

/**
 * Send a check-in message with streaming response via SSE.
 * Mirrors the SSE-decoding pattern from relationshipChat.ts.
 */
export async function streamCheckInMessage(
  checkInId: string,
  message: string,
  callbacks: CheckInStreamingCallbacks
): Promise<void> {
  const token = await getCurrentAccessToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE_URL}${API_PREFIX}/check-ins/${checkInId}/messages`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ content: message }),
      credentials: "include",
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send check-in message: ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  const processSSELines = (lines: string[]) => {
    let currentEvent = "";
    let currentData = "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7);
      } else if (line.startsWith("data: ")) {
        currentData = line.slice(6);
      } else if (line === "" && currentEvent && currentData) {
        try {
          const data = JSON.parse(currentData) as Record<string, unknown>;

          switch (currentEvent) {
            case "content":
              callbacks.onContent?.(data.content as string);
              break;
            case "done":
              callbacks.onDone?.({
                auto_finalized: data.auto_finalized as boolean,
                proposed_summary: data.proposed_summary as string | null,
                proposed_progress_signal: data.proposed_progress_signal as
                  | string
                  | null,
              });
              break;
            case "error":
              callbacks.onError?.(data.error as string);
              break;
          }
        } catch (e) {
          console.error("Failed to parse SSE data:", e);
        }

        currentEvent = "";
        currentData = "";
      }
    }
  };

  try {
    let reading = true;
    while (reading) {
      const { done, value } = await reader.read();

      if (done) {
        reading = false;
      }

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      processSSELines(lines);
    }

    if (buffer) {
      const finalLines = buffer.split("\n");
      finalLines.push("");
      processSSELines(finalLines);
    }
  } finally {
    reader.releaseLock();
  }
}
